// Failed-send state for the Marcus chat surface.
//
// The send path (`useChatWorker.sendMessage`/`createConversation`) posts to
// `/planner` via a raw axios instance (no response interceptor — see
// `@/hooks/useAxios`), so an HTTP failure rejects with a raw AxiosError that
// still carries `error.response.status` and `error.response.data`. Logical
// failures (`throw new Error('Message failed to send' | 'No conversation
// returned from server')`) reject with a generic Error that has no status.
//
// `toFailedMessage` normalizes both shapes so the UI can render an honest,
// status-aware error line instead of a blanket "server unavailable" string.

export interface FailedMessage {
  /** The user's message text, re-displayed greyed out under the input. */
  text: string;
  /** HTTP status when the rejection was an axios error, else undefined. */
  status?: number;
  /** Backend-supplied error body (only when it is a non-empty string). */
  message?: string;
}

const GENERIC_SEND_FAILURE =
  'Something went wrong sending your message. Please try again.';

const CONVERSATION_NOT_FOUND =
  'This conversation no longer exists. Start a new conversation.';

function errorResponse(error: unknown): Record<string, unknown> | undefined {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: unknown }).response;
    if (response && typeof response === 'object') {
      return response as Record<string, unknown>;
    }
  }
  return undefined;
}

function extractStatus(error: unknown): number | undefined {
  const status = errorResponse(error)?.status;
  return typeof status === 'number' ? status : undefined;
}

function extractBackendMessage(error: unknown): string | undefined {
  const data = errorResponse(error)?.data;
  if (typeof data === 'string' && data.trim()) {
    return data;
  }
  return undefined;
}

/** Build the failed-message state from the user's text + the send rejection. */
export function toFailedMessage(text: string, error: unknown): FailedMessage {
  return {
    text,
    status: extractStatus(error),
    message: extractBackendMessage(error),
  };
}

/**
 * Resolve the user-facing error line for a failed send.
 *
 * - 404 → the conversation is gone (the Marcus dead-conversation case).
 * - other client 4xx → the backend-supplied message (honest, actionable).
 * - 5xx / unknown / no status → a generic fallback; server internals are
 *   NEVER surfaced (a 5xx body may leak stack traces / infra detail).
 */
export function failedMessageErrorCopy(failed: FailedMessage): string {
  const { status, message } = failed;

  if (status === 404) {
    return CONVERSATION_NOT_FOUND;
  }

  if (status !== undefined && status >= 400 && status < 500 && message) {
    return message;
  }

  return GENERIC_SEND_FAILURE;
}
